/**
 * BhiSankeyCard — поток „рискови фактори → сигнални пътища → клинични резултати".
 *
 * Данни: GET /api/bhi/sankey (d3-sankey layout се изчислява в браузъра).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { sankey as d3sankey, sankeyLinkHorizontal, sankeyJustify } from "d3-sankey";
import { GitBranch, Info, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const NODE_W = 13;
const NODE_PAD = 14;

export default function BhiSankeyCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const wrapRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(900);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bhi/sankey`, { timeout: 40000 });
        if (alive) setData(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [API]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(320, el.clientWidth)));
    ro.observe(el);
    setWidth(Math.max(320, el.clientWidth));
    return () => ro.disconnect();
  }, [data]);

  const layout = useMemo(() => {
    if (!data || data.empty || !data.nodes?.length) return null;
    const height = Math.max(320, Math.min(620, data.nodes.filter((n) => n.column === "factor").length * 58));
    const nodes = data.nodes.map((n) => ({ ...n }));
    const index = new Map(nodes.map((n, i) => [n.id, i]));
    const links = data.links
      .filter((l) => index.has(l.source) && index.has(l.target))
      .map((l) => ({ ...l, source: index.get(l.source), target: index.get(l.target) }));
    if (!links.length) return null;
    const gen = d3sankey()
      .nodeWidth(NODE_W)
      .nodePadding(NODE_PAD)
      .nodeAlign(sankeyJustify)
      .extent([[8, 12], [width - 8, height - 12]]);
    try {
      return { ...gen({ nodes, links }), height };
    } catch {
      return null;
    }
  }, [data, width]);

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-600";

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl border ${card}`} data-testid="bhi-sankey-loading">
        <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="bhi-sankey-card">
      <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-pink-500" />
          <h2 className="text-sm font-bold">Откъде идва рискът: фактори → пътища → резултат</h2>
        </div>
        {data.dominant_outcome_bg && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-500"
            data-testid="bhi-sankey-dominant">
            Водещо: {data.dominant_outcome_bg}
          </span>
        )}
      </div>
      <p className={`text-[11px] mb-3 ${sub}`}>
        Дебелината на потока = приносът на фактора. {data.modifiable_share_pct}% от натоварването идва от
        фактори, които можете да променяте.
      </p>

      <div ref={wrapRef} className="w-full overflow-hidden">
        {!layout ? (
          <div className="p-3 rounded-xl bg-amber-500/10 text-amber-600 text-[11px]" data-testid="bhi-sankey-empty">
            Попълнете рисковите фактори за гърда, за да изчислим потока на риска.
          </div>
        ) : (
          <svg width={width} height={layout.height} data-testid="bhi-sankey-svg" role="img">
            <defs>
              <linearGradient id="sankey-fade" x1="0" x2="1">
                <stop offset="0%" stopColor="#EC4899" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            <g>
              {layout.links.map((l, i) => {
                const active = hover && (hover === l.source.id || hover === l.target.id);
                return (
                  <path
                    key={i}
                    d={sankeyLinkHorizontal()(l)}
                    fill="none"
                    stroke={l.color || "url(#sankey-fade)"}
                    strokeOpacity={active ? 0.75 : hover ? 0.12 : 0.32}
                    strokeWidth={Math.max(1.2, l.width)}
                    style={{ transition: "stroke-opacity 180ms ease" }}
                  />
                );
              })}
            </g>
            <g>
              {layout.nodes.map((n) => {
                const isLast = n.column === "outcome";
                const label = n.name_bg;
                return (
                  <g key={n.id}
                    onMouseEnter={() => setHover(n.id)}
                    onMouseLeave={() => setHover(null)}
                    data-testid={`bhi-sankey-node-${n.id}`}
                    style={{ cursor: "default" }}>
                    <rect
                      x={n.x0} y={n.y0}
                      width={Math.max(2, n.x1 - n.x0)}
                      height={Math.max(2, n.y1 - n.y0)}
                      fill={n.color}
                      rx={3}
                      opacity={hover && hover !== n.id ? 0.45 : 1}
                      style={{ transition: "opacity 180ms ease" }}
                    />
                    <text
                      x={isLast ? n.x0 - 6 : n.x1 + 6}
                      y={(n.y0 + n.y1) / 2}
                      dy="0.35em"
                      textAnchor={isLast ? "end" : "start"}
                      fontSize={10}
                      fontWeight={n.column === "factor" ? 600 : 500}
                      fill={isDark ? "#CBD5E1" : "#334155"}
                    >
                      {label.length > 30 ? `${label.slice(0, 29)}…` : label}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>

      {layout && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bhi-sankey-legend">
          {Object.entries(data.outcome_share).map(([oid, pct]) => {
            const node = data.nodes.find((n) => n.id === `o:${oid}`);
            if (!node) return null;
            return (
              <span key={oid}
                className="text-[10px] font-semibold px-2 py-1 rounded-full"
                style={{ background: `${node.color}22`, color: node.color }}
                data-testid={`bhi-sankey-share-${oid}`}>
                {node.name_bg} · {pct}%
              </span>
            );
          })}
        </div>
      )}

      {data.protective_pathways?.length > 0 && (
        <div className={`mt-2 text-[10px] flex items-start gap-1 ${sub}`} data-testid="bhi-sankey-protective">
          <Info className="w-3 h-3 mt-0.5 shrink-0 text-emerald-500" />
          <span>
            Защитни ефекти вече работят по: {data.protective_pathways.map((p) => p.name_bg).join(", ")}.
          </span>
        </div>
      )}
      <div className={`mt-2 text-[9px] ${sub}`}>{data.disclaimer_bg}</div>
    </div>
  );
}
